/**
 * Helper utilities for employee ordering, designation ranking, and marker handling.
 */

export const DEAN_MARKER_REGEX = /\[\[DEAN:([^\]]*)\]\]/;
export const ORDER_MARKER_REGEX = /\[\[ORDER:(\d+)\]\]/;

/**
 * Extract numerical custom order for an employee.
 * Checks direct property `order` / `sort_order`, then falls back to `[[ORDER:n]]` inside interests.
 */
export function extractEmployeeOrder(employee) {
  if (!employee) return null;

  if (typeof employee === "string") {
    const match = employee.match(ORDER_MARKER_REGEX);
    return match ? Number(match[1]) : null;
  }

  if (employee.order !== undefined && employee.order !== null && employee.order !== "") {
    const n = Number(employee.order);
    if (!isNaN(n)) return n;
  }

  if (employee.sort_order !== undefined && employee.sort_order !== null && employee.sort_order !== "") {
    const n = Number(employee.sort_order);
    if (!isNaN(n)) return n;
  }

  const interests = employee.interests || "";
  const match = String(interests).match(ORDER_MARKER_REGEX);
  if (match) {
    const n = Number(match[1]);
    if (!isNaN(n)) return n;
  }

  return null;
}

/**
 * Remove all internal system markers ([[DEAN:...]], [[ORDER:...]]) from interests string.
 */
export function stripEmployeeMarkers(interests = "") {
  return String(interests || "")
    .replace(/\[\[DEAN:[^\]]*\]\]/g, "")
    .replace(/\[\[ORDER:\d+\]\]/g, "")
    .replace(/^,\s*|\s*,\s*$/g, "")
    .replace(/,\s*,/g, ",")
    .trim();
}

/**
 * Rebuild interests string preserving or setting dean and order markers.
 */
export function withEmployeeMarkers(interests = "", deanOfFacultyId = "", order = null) {
  const clean = stripEmployeeMarkers(interests);
  const parts = [];

  if (deanOfFacultyId !== undefined && deanOfFacultyId !== null && deanOfFacultyId !== "") {
    parts.push(`[[DEAN:${deanOfFacultyId}]]`);
  }

  if (order !== undefined && order !== null && order !== "") {
    parts.push(`[[ORDER:${order}]]`);
  }

  if (clean) {
    parts.push(clean);
  }

  return parts.join(", ");
}

/**
 * Designation ranking:
 * 1 = Dean
 * 2 = Professor
 * 3 = Associate Professor
 * 4 = Assistant Professor
 * 5 = Lecturer
 * 6 = Visiting Faculty
 * 50 = Other designation
 * 999 = Empty / Unset
 */
export function getDesignationRank(employee) {
  if (!employee) return 999;

  const designation = (employee.designation || "").trim().toLowerCase();
  const prefix = (employee.prefix || "").trim().toLowerCase();

  const matchTitle = (text) => {
    if (!text) return 0;
    if (text.includes("dean")) return 1;
    if (
      text.includes("associate professor") ||
      text.includes("assoc. prof") ||
      text.includes("assoc prof")
    ) {
      return 3;
    }
    if (
      text.includes("assistant professor") ||
      text.includes("asst. prof") ||
      text.includes("asst prof")
    ) {
      return 4;
    }
    if (
      text.includes("professor") ||
      text.includes("prof.") ||
      text === "prof"
    ) {
      return 2;
    }
    if (text.includes("lecturer")) return 5;
    if (text.includes("visiting")) return 6;
    return 0;
  };

  const rankFromDesig = matchTitle(designation);
  if (rankFromDesig > 0) return rankFromDesig;

  const rankFromPrefix = matchTitle(prefix);
  if (rankFromPrefix > 0) return rankFromPrefix;

  if (designation) return 50;
  return 999;
}

/**
 * Comparator to sort employees within a category or department:
 * 1. Designation rank (Professor -> Associate -> Assistant -> Lecturer)
 * 2. Custom Order (if set, 1 comes before 2)
 * 3. Experience seniority (earlier date comes first)
 * 4. Alphabetical name
 */
export function sortEmployeesComparator(a, b) {
  const rankA = getDesignationRank(a);
  const rankB = getDesignationRank(b);

  if (rankA !== rankB) {
    return rankA - rankB;
  }

  const orderA = extractEmployeeOrder(a);
  const orderB = extractEmployeeOrder(b);

  if (orderA !== null && orderB !== null && orderA !== orderB) {
    return orderA - orderB;
  }
  if (orderA !== null && orderB === null) return -1;
  if (orderA === null && orderB !== null) return 1;

  const expA = a?.experienceSince ? new Date(a.experienceSince).getTime() : NaN;
  const expB = b?.experienceSince ? new Date(b.experienceSince).getTime() : NaN;
  const hasExpA = !isNaN(expA);
  const hasExpB = !isNaN(expB);

  if (hasExpA && hasExpB && expA !== expB) {
    return expA - expB;
  }
  if (hasExpA && !hasExpB) return -1;
  if (!hasExpA && hasExpB) return 1;

  const nameA = `${a?.firstName || ""} ${a?.lastName || ""}`.trim().toLowerCase();
  const nameB = `${b?.firstName || ""} ${b?.lastName || ""}`.trim().toLowerCase();

  return nameA.localeCompare(nameB);
}

/**
 * Group department employees into designation categories:
 * - professors
 * - associateProfessors
 * - assistantProfessors
 * - lecturers
 * - otherMembers
 */
export function categorizeDepartmentEmployees(employeesList = []) {
  const professors = [];
  const associateProfessors = [];
  const assistantProfessors = [];
  const lecturers = [];
  const otherMembers = [];

  const sorted = [...employeesList].sort(sortEmployeesComparator);

  sorted.forEach((employee) => {
    const rank = getDesignationRank(employee);
    if (rank === 2) {
      professors.push(employee);
    } else if (rank === 3) {
      associateProfessors.push(employee);
    } else if (rank === 4) {
      assistantProfessors.push(employee);
    } else if (rank === 5) {
      lecturers.push(employee);
    } else {
      otherMembers.push(employee);
    }
  });

  return {
    professors,
    associateProfessors,
    assistantProfessors,
    lecturers,
    otherMembers,
  };
}

